// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useState } from 'react';

/**
 * One row of the admin data-analysis view: any mapped feature, including the kinds the
 * rangefinder path deliberately drops (bunkers, water).
 *
 * Kept separate from useHoleFeatures on purpose — that hook feeds distance-critical UI
 * and filters to tee/green; this one is diagnostic and shows everything with provenance.
 */
export type AnalysisFeature = {
  id: string;
  holeNumber: number;
  kind: string;
  ordinal: number;
  name: string | null;
  lat: number;
  lon: number;
  /** `osm`, `gps` or `satellite`. */
  source: string;
  /** Imagery provider behind a satellite row; null otherwise. */
  sourceProvider: string | null;
  confidence: number;
  sampleCount: number;
  isReviewed: boolean;
  /** Boundary geometry stored, not just a centre point. */
  hasPolygon: boolean;
  /** The boundary's outer ring, when geometry is stored. */
  ring: { lat: number; lon: number }[] | null;
};

export type HoleAnalysis = {
  /** 0 means "not attributed to any hole". */
  holeNumber: number;
  features: AnalysisFeature[];
  /** Distinct players who flagged this hole's data as wrong. */
  inaccuracyReports: number;
};

type Row = Record<string, unknown>;

const num = (row: Row, ...keys: string[]): number => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 0;
};

const str = (row: Row, ...keys: string[]): string => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return '';
};

/**
 * Outer ring of a GeoJSON Polygon geometry, as lat/lon points.
 *
 * Defensive by design: the geometry column is written by the analysis service today but
 * is schema-free, so anything unexpected degrades to "no ring" rather than a crash.
 */
export const parsePolygonRing = (geoJson: string | null): { lat: number; lon: number }[] | null => {
  if (!geoJson) return null;
  try {
    const parsed = JSON.parse(geoJson) as {
      type?: unknown;
      coordinates?: unknown;
    };
    if (parsed.type !== 'Polygon' || !Array.isArray(parsed.coordinates)) return null;
    const outer = parsed.coordinates[0];
    if (!Array.isArray(outer)) return null;

    const ring: { lat: number; lon: number }[] = [];
    for (const position of outer) {
      if (!Array.isArray(position) || position.length < 2) return null;
      const [lon, lat] = position; // GeoJSON order
      if (typeof lon !== 'number' || typeof lat !== 'number') return null;
      ring.push({ lat, lon });
    }
    return ring.length >= 3 ? ring : null;
  } catch {
    return null;
  }
};

/** Payload casing varies across the services this passes through, so read both. */
export const mapAnalysisFeature = (row: Row): AnalysisFeature | null => {
  const id = str(row, 'id', 'Id');
  const kind = str(row, 'kind', 'Kind');
  if (!id || !kind) return null;

  return {
    id,
    holeNumber: num(row, 'holeNumber', 'HoleNumber'),
    kind,
    ordinal: num(row, 'ordinal', 'Ordinal'),
    name: str(row, 'name', 'Name') || null,
    lat: num(row, 'lat', 'Lat'),
    lon: num(row, 'lon', 'Lon'),
    source: str(row, 'source', 'Source') || 'gps',
    sourceProvider: str(row, 'sourceProvider', 'SourceProvider') || null,
    confidence: num(row, 'confidence', 'Confidence'),
    sampleCount: num(row, 'sampleCount', 'SampleCount'),
    isReviewed: row.isReviewed === true || row.IsReviewed === true,
    hasPolygon: row.hasPolygon === true || row.HasPolygon === true,
    ring: parsePolygonRing(str(row, 'polygonGeoJson', 'PolygonGeoJson') || null),
  };
};

/**
 * Groups features and report counts per hole. A hole appears if it has either features
 * or reports — a flagged hole with no data at all is exactly what an admin needs to see.
 */
export const buildHoleAnalysis = (
  features: AnalysisFeature[],
  reports: { holeNumber: number; count: number }[]
): HoleAnalysis[] => {
  const byHole = new Map<number, HoleAnalysis>();

  const entry = (holeNumber: number): HoleAnalysis => {
    let existing = byHole.get(holeNumber);
    if (!existing) {
      existing = { holeNumber, features: [], inaccuracyReports: 0 };
      byHole.set(holeNumber, existing);
    }
    return existing;
  };

  for (const feature of features) entry(feature.holeNumber).features.push(feature);
  for (const report of reports) entry(report.holeNumber).inaccuracyReports = report.count;

  // Objects first, cover last: a fairway or tree row says the least about a hole and is
  // the one an admin scrolls past, so it does not sit between the tee and the bunkers.
  const kindOrder = new Map([
    ['tee', 0],
    ['green', 1],
    ['bunker', 2],
    ['water', 3],
    ['fairway', 4],
    ['trees', 5],
  ]);
  for (const hole of byHole.values()) {
    hole.features.sort(
      (a, b) =>
        (kindOrder.get(a.kind) ?? 9) - (kindOrder.get(b.kind) ?? 9) || a.ordinal - b.ordinal
    );
  }

  return [...byHole.values()].sort((a, b) => a.holeNumber - b.holeNumber);
};

export type CourseAnalysisState = {
  holes: HoleAnalysis[];
  loading: boolean;
  failed: boolean;
  refresh: () => void;
};

export function useCourseAnalysis(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null
): CourseAnalysisState {
  const [holes, setHoles] = useState<HoleAnalysis[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!token || !courseId) {
      setHoles([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setFailed(false);
    const headers = { Authorization: `Bearer ${token}` };
    const base = `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}`;

    const load = async () => {
      try {
        const [featureResponse, reportResponse] = await Promise.all([
          fetch(`${base}/features`, { headers }),
          fetch(`${base}/inaccuracy-reports`, { headers }),
        ]);
        if (cancelled) return;
        if (!featureResponse.ok) {
          setHoles([]);
          setFailed(true);
          return;
        }

        const featureRows = (await featureResponse.json()) as Row[];
        // Report counts are admin-gated; a 403 here means a non-admin somehow reached
        // the screen — show the features with zero counts rather than nothing.
        const reportRows = reportResponse.ok ? ((await reportResponse.json()) as Row[]) : [];
        if (cancelled) return;

        const features = (Array.isArray(featureRows) ? featureRows : [])
          .map(mapAnalysisFeature)
          .filter((f): f is AnalysisFeature => f !== null);
        const reports = (Array.isArray(reportRows) ? reportRows : []).map((row) => ({
          holeNumber: num(row, 'holeNumber', 'HoleNumber'),
          count: num(row, 'count', 'Count'),
        }));

        setHoles(buildHoleAnalysis(features, reports));
      } catch {
        if (!cancelled) {
          setHoles([]);
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, token, reloadKey]);

  return { holes, loading, failed, refresh };
}

/** Flags a hole's mapped data as inaccurate. Any signed-in user; dedupes server-side. */
export async function reportHoleInaccurate(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
  holeNumber: number
): Promise<boolean> {
  if (!token || !courseId) return false;
  try {
    const response = await fetch(
      `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/holes/${holeNumber}/report-inaccurate`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
    );
    return response.ok;
  } catch {
    return false;
  }
}
